import { defineStore } from "pinia";
import type {
  ProductBulkMonitoringInput,
  ProductBulkMonitoringPreview,
  ProductBulkMonitoringResult,
} from "@price-rescue/api-contracts";
import { PRODUCT_MONITORING_OUTCOMES } from "@price-rescue/domain/product-monitoring";
import type { ProductMonitoringAction } from "@price-rescue/domain/product-monitoring";
import type { MonitoringCadence } from "@price-rescue/domain";
import { MerchantApiError } from "../authentication/errors.js";
import { freezeProductBulkSelection } from "./product-bulk-selection.js";
import type { ProductBulkMonitoringService } from "./product-bulk-monitoring.js";

export const useProductBulkMonitoringStore = defineStore("product-bulk-monitoring", {
  state: (): {
    service?: ProductBulkMonitoringService;
    selection: string[];
    action?: ProductMonitoringAction;
    preview?: ProductBulkMonitoringPreview;
    result?: ProductBulkMonitoringResult;
    error?: string;
    submitting: boolean;
    needsRefresh: boolean;
    pendingRequest?: { input: ProductBulkMonitoringInput; idempotencyKey: string };
  } => ({ selection: [], submitting: false, needsRefresh: false }),
  actions: {
    configure(service: ProductBulkMonitoringService): void {
      this.service = service;
    },
    async prepare(productIds: string[], action: ProductMonitoringAction): Promise<void> {
      if (this.submitting || this.needsRefresh) return;
      delete this.error;
      delete this.preview;
      delete this.result;
      this.submitting = true;
      try {
        this.selection = freezeProductBulkSelection(productIds);
        this.action = action;
        if (!this.service) throw new Error("Mapping monitoring is unavailable.");
        this.preview = await this.service.preview({ productIds: [...this.selection], action });
      } catch (error: unknown) {
        this.error =
          error instanceof Error ? error.message : "Could not preview mapping monitoring.";
      } finally {
        this.submitting = false;
      }
    },
    async execute(
      schedule: { cadence?: MonitoringCadence; timezone?: string },
      refresh: () => Promise<void>,
    ): Promise<void> {
      if (this.submitting || this.needsRefresh || !this.preview || !this.service || !this.action)
        return;
      this.pendingRequest = {
        input: { productIds: [...this.selection], action: this.action, ...schedule },
        idempotencyKey: globalThis.crypto.randomUUID(),
      };
      await this.reconcile(refresh);
    },
    async reconcile(refresh: () => Promise<void>): Promise<void> {
      if (this.submitting) return;
      const wasUncertain = this.needsRefresh && this.pendingRequest !== undefined;
      this.submitting = true;
      delete this.error;
      try {
        if (this.pendingRequest) {
          if (!this.service) throw new Error("Monitoring reconciliation is unavailable.");
          const pending = this.pendingRequest;
          this.result = await this.service.execute(pending.input, pending.idempotencyKey);
          if (!this.result.unconfirmed.length) delete this.pendingRequest;
        }
        delete this.preview;
        this.needsRefresh = true;
        await this.refreshProducts(refresh);
      } catch (error: unknown) {
        this.error =
          error instanceof Error ? error.message : "Monitoring changes could not be confirmed.";
        const definiteRejection =
          error instanceof MerchantApiError &&
          !["network", "server", "unknown"].includes(error.kind);
        if (definiteRejection && !wasUncertain) delete this.pendingRequest;
        this.needsRefresh = !definiteRejection || wasUncertain;
        if (this.needsRefresh && !this.result) {
          this.result = {
            updated: [],
            failed: [],
            skipped: [],
            unconfirmed: this.selection.map((productId) => ({
              productId,
              code: PRODUCT_MONITORING_OUTCOMES.UNCONFIRMED,
              message:
                "Some mapping schedules may have changed. Reconcile the original operation before retrying.",
            })),
          };
          delete this.preview;
        }
        if (this.needsRefresh) this.error += " Review the original operation before another batch.";
      } finally {
        this.submitting = false;
      }
    },
    async refreshProducts(refresh: () => Promise<void>): Promise<void> {
      try {
        await refresh();
        this.needsRefresh = this.pendingRequest !== undefined;
        if (this.needsRefresh)
          this.error =
            "The original monitoring operation is unconfirmed. Refresh its results before another batch.";
      } catch {
        this.needsRefresh = true;
        this.error = "Refresh products and review monitoring results before another batch.";
      }
    },
  },
});
